import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { inr, downloadCSV } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const REPORTS = [
  { group: "Accounts", ownerOnly: true },
  { id: "profit", label: "Profit & Loss", ownerOnly: true },
  { id: "gst-summary", label: "GST Summary", ownerOnly: true },
  { id: "expenses", label: "Expense Register", ownerOnly: true },
  { id: "purchase-vs-sales", label: "Sales vs Purchases vs Expenses", ownerOnly: true },
  { id: "vendor-payable", label: "Vendor Payable", ownerOnly: true },
  { group: "Sales" },
  { id: "sales-summary", label: "Sales Register" },
  { id: "tax-collected", label: "GST Collected by Invoice" },
  { id: "customer-outstanding", label: "Customer Outstanding" },
  { id: "top-products", label: "Top Products", ownerOnly: true },
  { group: "Stock" },
  { id: "inventory-valuation", label: "Inventory Valuation", ownerOnly: true },
  { id: "stock-on-hand", label: "Stock on Hand" },
  { id: "low-stock", label: "Low Stock" },
  { id: "dead-stock", label: "Dead Stock / Aging" },
];

const MONEY_COLS = ["total", "paid", "balance", "value", "cost", "value_cost", "value_retail", "taxable", "tax", "outstanding", "total_spent", "payable", "revenue", "margin", "amount", "gst"];
const money = (v) => (v < 0 ? `(${inr(-v)})` : inr(v));

export default function Reports() {
  const { isOwner } = useAuth();
  const avail = REPORTS.filter((r) => !r.ownerOnly || isOwner);
  const [active, setActive] = useState(isOwner ? "profit" : "sales-summary");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const qs = [];
    if (start) qs.push(`start=${start}`);
    if (end) qs.push(`end=${end}`);
    setData(null);
    const r = await api.get(`/reports/${active}${qs.length ? "?" + qs.join("&") : ""}`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [active, start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = avail.find((r) => r.id === active)?.label || "";

  return (
    <div>
      <PageHeader eyebrow="Accounting & analytics" title="Reports" subtitle="Filter by date and export any report to CSV.">
        <button className="btn btn-gold" disabled={!data?.rows?.length} onClick={() => downloadCSV(`${active}.csv`, data.columns, data.rows)} data-testid="export-csv">
          <Download size={16} /> Export CSV
        </button>
      </PageHeader>

      <div className="flex gap-4 flex-wrap">
        <div className="w-56 shrink-0 space-y-1">
          {avail.map((r) => r.group ? <div key={r.group} className="eyebrow px-3.5 pt-3 pb-1">{r.group}</div> : (
            <button key={r.id} onClick={() => setActive(r.id)} data-testid={`report-${r.id}`}
              className={`w-full text-left px-3.5 py-2.5 rounded-lg text-sm transition-colors ${active === r.id ? "bg-[#0E1B2E] text-white" : "text-[#5C626A] hover:bg-[#F4F0EA]"}`}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-[320px]">
          <div className="flex items-end gap-3 mb-4">
            <div><label className="label">From</label><input type="date" className="field" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" /></div>
            <div><label className="label">To</label><input type="date" className="field" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" /></div>
            {(start || end) && <button className="btn btn-ghost btn-sm" onClick={() => { setStart(""); setEnd(""); }}>Clear</button>}
          </div>

          <div className="card overflow-hidden">
            <div className="px-5 py-3.5 border-b border-[#E6E1D8]"><h3 className="font-display text-lg">{label}</h3></div>
            {data && (
              <div className="overflow-auto max-h-[60vh]">
                <table className="tbl">
                  <thead>
                    <tr>{data.columns.map((c) => <th key={c} className={MONEY_COLS.includes(c) ? "num" : ""}>{c.replace(/_/g, " ")}</th>)}</tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => {
                      const strong = typeof row.metric === "string" && /^(Net sales|Gross profit|Net profit|Total expenses|Net GST payable)/.test(row.metric);
                      return (
                        <tr key={i} className={strong ? "font-semibold bg-[#FCFBF8]" : ""}>
                          {data.columns.map((c) => (
                            <td key={c} className={MONEY_COLS.includes(c) ? "num font-num" : ""}>
                              {MONEY_COLS.includes(c) && typeof row[c] === "number" ? money(row[c]) : String(row[c] ?? "")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {data.rows.length === 0 && <Empty>No data for this period</Empty>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
